import { MAX_ALLOWED_PETS } from '@rover/constants/src/search/searchPage.constants';
import { Pet, SearchFilters } from '@rover/types';
import { PetType } from '@rover/types/src/Pet';

type PetTypeCounts = Pick<SearchFilters, 'dogCount' | 'catCount' | 'puppyCount'>;

export const updatePetCountersFromOwnPets = (
  searchFilters: Partial<SearchFilters>,
  pets: Pet[] = []
): PetTypeCounts => {
  if (!searchFilters || !searchFilters.pet || pets.length <= 0) {
    return {};
  }

  const selectedPets =
    pets.filter((pet: Pet) => {
      return searchFilters?.pet?.includes(pet.pk.toString());
    }) ?? [];

  return {
    puppyCount: selectedPets.filter((pet) => pet.petType === PetType.DOG && pet.isPuppy).length,
    dogCount: selectedPets.filter((pet) => pet.petType === PetType.DOG && !pet.isPuppy).length,
    catCount: selectedPets.filter((pet) => pet.petType === PetType.CAT).length,
  };
};

export const getSelectedPetCount = (
  petType: PetType,
  defaultToZero: boolean,
  petTypes: PetType[] = [],
  selectedPetCount = 0,
  hasCounterQueryParams = false
): number => {
  if (defaultToZero && selectedPetCount === 0) {
    return 0;
  }

  if (petTypes?.includes(petType) && !hasCounterQueryParams) {
    return 1;
  }

  return selectedPetCount;
};

export const adjustPetCountsToMaxAllowed = (petCounts: PetTypeCounts): PetTypeCounts => {
  let remainingPetCount = MAX_ALLOWED_PETS;

  const adjustedCounts = Object.entries(petCounts)
    .sort(([, count1], [, count2]) => count2 - count1) // sort by count in descending order
    .map(([key, count]) => {
      const newCount = count >= remainingPetCount ? remainingPetCount : count;
      remainingPetCount -= newCount;
      return [key, newCount];
    });

  return Object.fromEntries(adjustedCounts);
};

export const ensureAtLeastOneDog = (
  dogCount: number,
  catCount: number,
  puppyCount: number
): PetTypeCounts => {
  const totalCounts = dogCount + catCount + puppyCount;

  return {
    dogCount: totalCounts > 0 ? dogCount : 1,
    catCount,
    puppyCount,
  };
};

export const updatePetCountersFromPetTypeQueryParam = (
  searchFilters: Partial<SearchFilters>,
  defaultToZero: boolean
): PetTypeCounts => {
  const {
    petType,
    dogCount: searchDogCount = 0,
    catCount: searchCatCount = 0,
    puppyCount: searchPuppyCount = 0,
  } = searchFilters;
  const hasCounterQueryParams = searchDogCount + searchCatCount + searchPuppyCount > 0;
  const dogCount = getSelectedPetCount(
    PetType.DOG,
    defaultToZero,
    petType,
    searchDogCount,
    hasCounterQueryParams
  );
  const catCount = getSelectedPetCount(
    PetType.CAT,
    defaultToZero,
    petType,
    searchCatCount,
    hasCounterQueryParams
  );
  const puppyCount = getSelectedPetCount(
    PetType.PUPPY,
    defaultToZero,
    petType,
    searchPuppyCount,
    hasCounterQueryParams
  );

  if (defaultToZero) {
    return adjustPetCountsToMaxAllowed({ dogCount, catCount, puppyCount });
  }
  return adjustPetCountsToMaxAllowed(ensureAtLeastOneDog(dogCount, catCount, puppyCount));
};
